import { createPublicClient, http } from 'viem';
import invariant from 'tiny-invariant';
import { GASTANK_DEPLOYMENTS, PUBLIC_RPCS } from '../config/networks';
import { MaxSigDeadline } from '../../types/config/constants';
import { Permit2Abi } from '../../types/abis/Permit2Abi';
import { SupportedNetworks } from '../config/networkAddresses';
import { PERMIT_BATCH_TYPES } from '../../types/evm';
import { validatePermitDetails } from '../../types/evm';
import { CURRENT_VERSION } from '../config/environment';
import { isNativeToken } from '../../utils/validation/token-validation';
function convertBigIntToString(obj) {
    for (const key in obj) {
        if (typeof obj[key] === 'bigint') {
            obj[key] = obj[key].toString();
        }
        else if (typeof obj[key] === 'object') {
            convertBigIntToString(obj[key]);
        }
    }
}
/**
 * Builds the transfer details for batch transfer with permit2.
 * @param {IBuildBatchTransferDetails} options - The options for building the transfer details.
 * @returns {Promise<{ permitBatchDetails: PermitBatch, permitSignature: Hex }>} The permit batch details and permit signature.
 */
//TODO add option to only return permitBatchDetails if wallet is not provided so we can expose as api
export async function buildPermit2Details({ tokensIn, amounts, spender, network, ownerAccount, }) {
    const expirationSeconds = BigInt(60 * 60);
    const clientConfig = SupportedNetworks(CURRENT_VERSION)[network];
    const client = createPublicClient({
        chain: clientConfig.chain,
        transport: http(PUBLIC_RPCS[network]),
    });
    const { permit2 } = SupportedNetworks(CURRENT_VERSION)[network];
    const gasTank = GASTANK_DEPLOYMENTS[network];
    const { nonNativeTokens, nonNativeAmounts } = tokensIn.reduce((acc, token, index) => {
        if (isNativeToken(token)) {
            return acc;
        }
        acc.nonNativeTokens.push(token);
        acc.nonNativeAmounts.push(amounts[index]);
        return acc;
    }, { nonNativeTokens: [], nonNativeAmounts: [] });
    const permit2Infos = await client.multicall({
        // @ts-ignore
        contracts: nonNativeTokens.map((token) => {
            return {
                address: permit2,
                abi: Permit2Abi,
                functionName: 'allowance',
                args: [ownerAccount.address, token, gasTank],
            };
        }),
        allowFailure: false,
    });
    const nonces = permit2Infos.map((info) => info[2]);
    const permit2Details = nonNativeTokens.map((token, index) => ({
        token,
        amount: BigInt(nonNativeAmounts[index]),
        expiration: BigInt(Math.floor(Date.now() / 1000)) + expirationSeconds,
        nonce: BigInt(nonces[index]),
    }));
    const permitBatchDetails = {
        details: permit2Details,
        spender,
        sigDeadline: BigInt(Math.floor(Date.now() / 1000)) + expirationSeconds,
    };
    invariant(MaxSigDeadline.gte(permitBatchDetails.sigDeadline), 'SIG_DEADLINE_OUT_OF_RANGE');
    //@ts-ignore
    permitBatchDetails.details.forEach(validatePermitDetails);
    const domain = {
        name: 'Permit2',
        chainId: network,
        verifyingContract: permit2,
    };
    const permitSignature = await ownerAccount._signTypedData(domain, PERMIT_BATCH_TYPES, permitBatchDetails);
    convertBigIntToString(permitBatchDetails);
    return {
        permitBatchDetails,
        permitSignature: permitSignature,
    };
}
